import React, { memo, useState } from 'react';
import Box from '@mui/material/Box';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSettingsContext } from 'src/components/settings';
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import { NavPinnedProvider } from './nav-pinned-context';

// Memo pour éviter les rerenders inutiles
const MemoNavVertical = memo(NavVertical);
const MemoNavMini = memo(NavMini);
const MemoNavHorizontal = memo(NavHorizontal);

// Composant nav stable
function DashboardNav({
  layout,
  lgUp,
  open,
  onClose,
  onExpand,
  onPin,
}: {
  layout: string;
  lgUp: boolean;
  open: boolean;
  onClose: () => void;
  onExpand: (v: boolean) => void;
  onPin: (v: boolean) => void;
}) {
  if (layout === 'horizontal') {
    return lgUp ? <MemoNavHorizontal /> : <MemoNavVertical openNav={open} onCloseNav={onClose} onExpand={onExpand} onPin={onPin} />;
  }

  if (layout === 'mini') {
    // NavMini is not yet implemented — fall back to NavVertical on all sizes
    return <MemoNavVertical openNav={open} onCloseNav={onClose} onExpand={onExpand} onPin={onPin} />;
  }

  return <MemoNavVertical openNav={open} onCloseNav={onClose} onExpand={onExpand} onPin={onPin} />;
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const settings = useSettingsContext();
  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean();
  const [navExpanded, setNavExpanded] = useState(false);
  const [navPinned, setNavPinned] = useState(false);

  const isMini = settings.themeLayout === 'mini';

  // Box sx stables
  const containerSx = React.useMemo(
    () => ({
      minHeight: 1,
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      ...(isMini && { backgroundColor: 'rgba(247, 247, 247, 1)' }),
    }),
    [isMini]
  );

  return (
    <NavPinnedProvider value={navPinned}>
      <Box sx={{height: '100%'}}>
        <Header onOpenNav={nav.onTrue} navExpanded={navExpanded} navPinned={navPinned} />
        <Box sx={containerSx}>
          <DashboardNav
            layout={settings.themeLayout}
            lgUp={lgUp}
            open={nav.value}
            onClose={nav.onFalse}
            onExpand={setNavExpanded}
            onPin={setNavPinned}

          />
          <Main navPinned={navPinned}>{children}</Main>
        </Box>
      </Box>
    </NavPinnedProvider>
  );
}
